/**
 * The sentences for the homework screens (#376, #445), in English and Hebrew, keyed by the contract's own
 * enumerations and error codes and by nothing the server wrote.
 *
 * Two rules the wording follows, because the screen it serves exists to hold
 * them:
 *
 * - **Nothing here judges the work.** The screen presents what was read, never
 *   whether it is right. No word below says correct, wrong, score, grade or
 *   feedback, and nothing is a proportion (`no-percentage.test.tsx`).
 * - **A reader's text is untrusted handwriting content.** These are the
 *   screen's own words about it; the text itself is rendered by the components
 *   as plain React children and never passes through this file.
 *
 * A message is a string or a function; a record-shaped wording is reached through a function (`stage(...)`).
 */

import type {
  ErrorCode,
  HomeworkTranscriptionResponse,
  HomeworkUploadResponse,
} from '@davinci/contracts';
import { defineMessages } from './messages.js';
import { useMessages, type UiLocale } from './locale.js';

type ReviewStage = HomeworkUploadResponse['pages'][number]['reviewStage'];
type UploadFailureKind = NonNullable<HomeworkUploadResponse['failureKind']>;
type Reader = HomeworkTranscriptionResponse['readers'][number];
type ReaderFailureKind = NonNullable<Reader['failureKind']>;
type Agreement = HomeworkTranscriptionResponse['regions'][number]['agreement'];
type QuestionLabelSource = HomeworkTranscriptionResponse['regions'][number]['questionLabelSource'];
type ExerciseState = 'not_posted' | 'posted' | 'checked' | 'fixed';
type RefusedReaderKind = Exclude<ReaderFailureKind, 'consent_required'>;

/** The limits the server enforces on one homework upload (BACKEND_DESIGN §20). */
const UPLOAD_LIMIT = {
  en: 'A homework PDF can be at most 25 MiB and 20 pages.',
  he: 'קובץ PDF של שיעורי בית יכול להיות עד 25 MiB ועד 20 עמודים.',
};

const STAGE_EN: Record<ReviewStage, string> = {
  reading: 'Being read',
  // #395: both readers failed, so there is nothing on the page to check. The reason is shown beside it.
  unreadable: 'Could not be read',
  awaiting_review: 'Needs your check',
  confirmed: 'Confirmed',
};
const STAGE_HE: Record<ReviewStage, string> = {
  reading: 'נקרא כעת',
  unreadable: 'לא ניתן היה לקרוא',
  awaiting_review: 'ממתין לבדיקה שלך',
  confirmed: 'אושר',
};

/**
 * Why a homework upload stopped. Pages are prepared and read one after another, so a failure on page N leaves pages
 * before N already sent to the readers (#251): none of these sentences may say "nothing was read" for a failure that
 * happens while preparing a page. `renderer_not_installed` fails before any page, so it makes no such claim.
 */
const UPLOAD_FAILURE_EN: Record<UploadFailureKind, string> = {
  page_too_large:
    'A page of this file could not be prepared because it is too large. Pages before it may already have been read.',
  pdf_unreadable:
    'DaVinci could not open this file as a PDF. Pages before the problem may already have been read.',
  render_failed:
    'DaVinci could not turn a page of this file into an image. Pages before it may already have been read.',
  renderer_not_installed:
    'The page renderer is not installed on this server, so no file can be read.',
  invalid_result:
    'DaVinci could not make sense of a rendered page. Pages before it may already have been read.',
  transport:
    "A page could not be prepared because of a fault between DaVinci's own parts. Pages before it may already have been read.",
  unclassified: 'DaVinci could not read this file, for a reason it could not name.',
};
const UPLOAD_FAILURE_HE: Record<UploadFailureKind, string> = {
  page_too_large: 'לא ניתן היה להכין עמוד בקובץ הזה כי הוא גדול מדי. ייתכן שעמודים שלפניו כבר נקראו.',
  pdf_unreadable: 'לא ניתן היה לפתוח את הקובץ הזה כ-PDF. ייתכן שעמודים שלפני הבעיה כבר נקראו.',
  render_failed: 'לא ניתן היה להפוך עמוד בקובץ הזה לתמונה. ייתכן שעמודים שלפניו כבר נקראו.',
  renderer_not_installed: 'רכיב עיבוד העמודים אינו מותקן בשרת הזה, ולכן אי אפשר לקרוא אף קובץ.',
  invalid_result: 'לא ניתן היה לעבד עמוד שהומר לתמונה. ייתכן שעמודים שלפניו כבר נקראו.',
  transport: 'לא ניתן היה להכין עמוד בגלל תקלה בין החלקים של DaVinci עצמה. ייתכן שעמודים שלפניו כבר נקראו.',
  unclassified: 'לא ניתן היה לקרוא את הקובץ הזה, ומסיבה שלא זוהתה.',
};

/** Reader failure kinds other than `consent_required`, which gets a link rather than a sentence. */
const READER_FAILURE_EN: Record<RefusedReaderKind, string> = {
  budget_refused: 'The reading budget for this semester is used up, so this page was not fully read.',
  api_key_missing: 'Reading is not set up on this server yet, so this page was not fully read. Tell whoever runs DaVinci.',
  paid_tier_unattested: 'Reading is not set up on this server yet, so this page was not fully read. Tell whoever runs DaVinci.',
  image_unreadable: 'This page is hard to make out. Try again, or post a clearer scan.',
  remote_call_failed: 'The reading did not go through. Try again.',
  response_blocked: 'This page could not be read. Try again, or post a clearer scan.',
  response_truncated: 'The reading did not finish. Try again.',
  empty_response: 'The reading did not finish. Try again.',
  invalid_output: 'The reading did not finish. Try again.',
  settings_echo_mismatch: 'The reading did not finish. Try again.',
  unclassified: 'This page was not fully read. Try again.',
};
const READER_FAILURE_HE: Record<RefusedReaderKind, string> = {
  budget_refused: 'תקציב הקריאה לסמסטר הזה מוצה, ולכן העמוד הזה לא נקרא עד הסוף.',
  api_key_missing: 'הקריאה עוד לא מוגדרת בשרת הזה, ולכן העמוד הזה לא נקרא עד הסוף. כדאי לפנות למי שמפעיל את DaVinci.',
  paid_tier_unattested: 'הקריאה עוד לא מוגדרת בשרת הזה, ולכן העמוד הזה לא נקרא עד הסוף. כדאי לפנות למי שמפעיל את DaVinci.',
  image_unreadable: 'קשה להבין את העמוד הזה. אפשר לנסות שוב, או להעלות סריקה ברורה יותר.',
  remote_call_failed: 'הקריאה לא הושלמה. אפשר לנסות שוב.',
  response_blocked: 'לא ניתן היה לקרוא את העמוד הזה. אפשר לנסות שוב, או להעלות סריקה ברורה יותר.',
  response_truncated: 'הקריאה לא הסתיימה. אפשר לנסות שוב.',
  empty_response: 'הקריאה לא הסתיימה. אפשר לנסות שוב.',
  invalid_output: 'הקריאה לא הסתיימה. אפשר לנסות שוב.',
  settings_echo_mismatch: 'הקריאה לא הסתיימה. אפשר לנסות שוב.',
  unclassified: 'העמוד הזה לא נקרא עד הסוף. אפשר לנסות שוב.',
};

const AGREEMENT_EN: Record<Agreement, string> = {
  agreed: 'Both readers read this the same way.',
  reader_uncertain: 'A reader was not sure what this line says, so it needs your check.',
  single_reader: 'Only one reader produced this line, so it needs your check.',
  disagreed: 'The two readers read this line differently. Only you can say what it says.',
};
const AGREEMENT_HE: Record<Agreement, string> = {
  agreed: 'שני הקוראים קראו את השורה הזו באותו אופן.',
  reader_uncertain: 'אחד הקוראים לא היה בטוח מה כתוב בשורה הזו, ולכן היא ממתינה לבדיקה שלך.',
  single_reader: 'רק קורא אחד הפיק את השורה הזו, ולכן היא ממתינה לבדיקה שלך.',
  disagreed: 'שני הקוראים קראו את השורה הזו באופן שונה. ההכרעה במה שכתוב בה היא שלך בלבד.',
};

const QUESTION_SOURCE_EN: Record<QuestionLabelSource, string> = {
  reader_marker: 'read from the start of this line',
  carried: 'continued from the line above',
  learner: 'you chose this',
  none: 'no question marked above',
};
const QUESTION_SOURCE_HE: Record<QuestionLabelSource, string> = {
  reader_marker: 'נקרא מתחילת השורה הזו',
  carried: 'המשך מהשורה שמעליה',
  learner: 'בחרת בזה בעצמך',
  none: 'לא סומנה שאלה למעלה',
};

const EXERCISE_STATE_EN: Record<ExerciseState, string> = {
  not_posted: 'not posted',
  posted: 'posted, not checked yet',
  checked: 'checked',
  fixed: 'fixed',
};
const EXERCISE_STATE_HE: Record<ExerciseState, string> = {
  not_posted: 'טרם פורסם',
  posted: 'פורסם, עדיין לא נבדק',
  checked: 'נבדק',
  fixed: 'תוקן',
};

function dueDate(intlLocale: string, dueAt: string): string {
  const parts = new Intl.DateTimeFormat(intlLocale, { weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(
    new Date(dueAt),
  );
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

function uploadFailureEn(code: ErrorCode | undefined): string {
  switch (code) {
    case 'reader_unavailable':
      return 'Reading handwriting is switched off on this server, so homework cannot be uploaded yet. Nothing was stored.';
    case 'upload_limit_exceeded':
    case 'payload_too_large':
      return `That file is over the limit, so nothing was stored. ${UPLOAD_LIMIT.en} Send fewer or smaller pages.`;
    case 'unsupported_media_type':
      return 'That file is not a PDF. Nothing was stored.';
    case 'homework_exercise_mismatch':
      return 'You already uploaded this exact file for a different homework. Nothing was stored. Choose that homework, or upload the right file.';
    default:
      return '';
  }
}
function uploadFailureHe(code: ErrorCode | undefined): string {
  switch (code) {
    case 'reader_unavailable':
      return 'קריאת כתב יד כבויה בשרת הזה, ולכן עדיין אי אפשר להעלות שיעורי בית. דבר לא נשמר.';
    case 'upload_limit_exceeded':
    case 'payload_too_large':
      return `הקובץ חורג מהמגבלה, ולכן דבר לא נשמר. ${UPLOAD_LIMIT.he} יש לשלוח פחות עמודים או עמודים קטנים יותר.`;
    case 'unsupported_media_type':
      return 'הקובץ אינו PDF. דבר לא נשמר.';
    case 'homework_exercise_mismatch':
      return 'כבר העלית את הקובץ המדויק הזה עבור שיעורי בית אחרים. דבר לא נשמר. אפשר לבחור את שיעורי הבית ההם, או להעלות את הקובץ הנכון.';
    default:
      return '';
  }
}

function reviewFailureEn(code: ErrorCode | undefined): string {
  switch (code) {
    case 'transcription_not_ready':
      return 'This page is still being read. Wait a moment, then try again.';
    case 'transcription_unresolved':
      return 'A line still needs the text you wrote. Type it, then try again.';
    case 'transcription_changed':
      return 'This page changed since you last looked at it. It has been reloaded: check it again, then confirm.';
    case 'validation_failed':
      return 'That text was not accepted. It cannot be empty and can be at most 4000 characters.';
    case 'line_has_a_refutation':
      return 'DaVinci already found a problem on this line, so it stays in the check. You can correct it instead.';
    default:
      return '';
  }
}
function reviewFailureHe(code: ErrorCode | undefined): string {
  switch (code) {
    case 'transcription_not_ready':
      return 'העמוד הזה עדיין נקרא. יש להמתין רגע ולנסות שוב.';
    case 'transcription_unresolved':
      return 'שורה עדיין זקוקה לטקסט שכתבת. יש להקליד אותו ולנסות שוב.';
    case 'transcription_changed':
      return 'העמוד הזה השתנה מאז שהסתכלת בו לאחרונה. הוא נטען מחדש: יש לבדוק אותו שוב ואז לאשר.';
    case 'validation_failed':
      return 'הטקסט לא התקבל. הוא לא יכול להיות ריק ואורכו עד 4000 תווים.';
    case 'line_has_a_refutation':
      return 'DaVinci כבר מצאה בעיה בשורה הזו, ולכן היא נשארת בבדיקה. אפשר לתקן אותה במקום זאת.';
    default:
      return '';
  }
}

function labelFailureEn(code: ErrorCode | undefined): string {
  switch (code) {
    case 'validation_failed':
      return 'That label was not accepted. It cannot be empty and can be at most 32 characters.';
    case 'transcription_unresolved':
      return 'This line is marked as not on your page, so it is not part of any question.';
    default:
      return reviewFailureEn(code);
  }
}
function labelFailureHe(code: ErrorCode | undefined): string {
  switch (code) {
    case 'validation_failed':
      return 'התווית לא התקבלה. היא לא יכולה להיות ריקה ואורכה עד 32 תווים.';
    case 'transcription_unresolved':
      return 'השורה הזו סומנה כלא-בדף שלך, ולכן היא אינה חלק משום שאלה.';
    default:
      return reviewFailureHe(code);
  }
}

/** The spoken names of the tiles on the reading editor's symbol palette (#660), by symbol. */
const SYMBOL_NAMES_EN: Readonly<Record<string, string>> = {
  'ℝ': 'real numbers',
  'ℕ': 'natural numbers',
  'ℤ': 'integers',
  '∈': 'element of',
  '∉': 'not an element of',
  '≤': 'less than or equal to',
  '≥': 'greater than or equal to',
  '≠': 'not equal to',
  '→': 'arrow',
  '⇒': 'implies',
  '∀': 'for all',
  '∃': 'there exists',
  'λ': 'lambda',
  'π': 'pi',
  'Σ': 'sum',
  '∫': 'integral',
  '√': 'square root',
  '²': 'squared',
  '⁻¹': 'inverse',
  '∞': 'infinity',
  '⊂': 'subset of',
  '∪': 'union',
  '∩': 'intersection',
  '≈': 'approximately equal to',
  '⊥': 'perpendicular to',
  '∂': 'partial derivative',
  'θ': 'theta',
};
const SYMBOL_NAMES_HE: Readonly<Record<string, string>> = {
  'ℝ': 'המספרים הממשיים',
  'ℕ': 'המספרים הטבעיים',
  'ℤ': 'המספרים השלמים',
  '∈': 'שייך ל',
  '∉': 'לא שייך ל',
  '≤': 'קטן או שווה',
  '≥': 'גדול או שווה',
  '≠': 'לא שווה',
  '→': 'חץ',
  '⇒': 'גורר',
  '∀': 'לכל',
  '∃': 'קיים',
  'λ': 'למדא',
  'π': 'פאי',
  'Σ': 'סכום',
  '∫': 'אינטגרל',
  '√': 'שורש ריבועי',
  '²': 'בריבוע',
  '⁻¹': 'הופכי',
  '∞': 'אינסוף',
  '⊂': 'מוכל ב',
  '∪': 'איחוד',
  '∩': 'חיתוך',
  '≈': 'שווה בקירוב',
  '⊥': 'ניצב ל',
  '∂': 'נגזרת חלקית',
  'θ': 'תטא',
};

export const homeworkLabels = defineMessages({
  en: {
    uploadLimit: UPLOAD_LIMIT.en,
    stage: (stage: ReviewStage): string => STAGE_EN[stage],
    uploadFailure: (kind: UploadFailureKind): string => UPLOAD_FAILURE_EN[kind],
    readerFailure: (kind: RefusedReaderKind): string => READER_FAILURE_EN[kind],
    agreement: (agreement: Agreement): string => AGREEMENT_EN[agreement],
    /**
     * A line the learner says is not on their page (#392): the third choice on a line with one candidate text, and what
     * the line shows once it is chosen. The wording states what the learner said and what happens to it; it does not say
     * the line was wrong or the learner right.
     */
    notOnMyPageAction: 'This line is not on my page',
    notOnMyPageLabel: 'Not on my page',
    notOnMyPageExplanation:
      'You said this line is not on your page, so it is left out. It stays on record, and you can undo this.',
    undo: 'Undo',
    /** The paired card for a text-identical pair that split into two lines (#392): one question, asked once. */
    pairQuestion: 'Is this one line or two?',
    pairExplanation: 'The readers found this same text in two places on your page, so it is shown as two lines.',
    pairOneLine: 'One line — I wrote it once',
    pairTwoLines: 'Two lines — I wrote it twice',
    pairNeither: 'Neither — show them separately',
    pairHeading: (first: number, second: number): string => `Lines ${String(first)} and ${String(second)}`,
    /** What happened to an upload attempt, by the server's code; `undefined` when the code is not one this screen knows. */
    uploadFailureSentence: uploadFailureEn,
    labelFailureSentence: labelFailureEn,
    reviewFailureSentence: reviewFailureEn,
    /**
     * Which question a line belongs to (#406 slice 3). The label is the learner's own marker read back to them, or what
     * they chose; these words only say where it came from, so the learner can see why a line is where it is and move it.
     */
    questionWord: 'Question',
    noQuestion: 'No question',
    questionSource: (source: QuestionLabelSource): string => QUESTION_SOURCE_EN[source],
    changeQuestion: 'Change question',
    questionFieldLabel: 'Which question is this line part of?',
    questionFieldHint: 'For example 3 or 5ב. Lines below this one follow it, until the next question you marked.',
    saveQuestion: 'Save question',
    notPartOfQuestion: 'Not part of a question',
    cancel: 'Cancel',
    /** The upload screen's exercise choice and "this week" strip (#437). Words only: no count, score or percentage. */
    chooseHomeworkLabel: 'Which homework?',
    chooseHomeworkMissing: 'Choose which homework this is.',
    noHomeworkSetUp: 'No homework is set up for your courses yet, so nothing can be uploaded.',
    thisWeekHeading: 'This week',
    earlierSheets: 'Earlier sheets',
    nothingDue: 'Nothing due this week.',
    exerciseState: (state: ExerciseState, solutionOpen: boolean): string =>
      solutionOpen ? `${EXERCISE_STATE_EN[state]}, solution open` : EXERCISE_STATE_EN[state],
    /** `Tue 20 Oct`, in the browser's calendar. */
    dueDate: (dueAt: string): string => dueDate('en-GB', dueAt),
    dueOn: (date: string): string => `due ${date}`,
    // --- reading, ready, check, done, failed (#573)
    readingTitle: 'Reading your pages',
    crumbsLabel: 'Where you are',
    pagesInUpload: (n: number): string => (n === 1 ? '1 page' : `${String(n)} pages`),
    ringUnit: 'pages',
    pagesReadOf: (read: number, total: number): string => `Pages read: ${String(read)}. Pages in this upload: ${String(total)}.`,
    pageTileState: (n: number, state: 'read' | 'reading' | 'waiting' | 'failed'): string =>
      `Page ${String(n)}: ${state === 'failed' ? 'could not be read' : state}`,
    timeLeft: (e: { readonly kind: 'minutes'; readonly minutes: number } | { readonly kind: 'under_a_minute' }): string =>
      e.kind === 'under_a_minute' ? 'Less than a minute left' : `About ${String(e.minutes)} ${e.minutes === 1 ? 'minute' : 'minutes'} left`,
    timeLeftBasis: 'Measured from the pages read so far',
    canLeave: 'You can leave. The check keeps going.',
    backToHomework: 'Back to Homework',
    readyTitle: (n: number): string => (n === 1 ? '1 thing needs your check' : `${String(n)} things need your check`),
    readyAcross: (p: number): string => (p === 1 ? 'On the 1 page' : `Across all ${String(p)} pages`),
    readyNothingLeft: 'Every line is read. Confirm each page to finish.',
    readyCounting: 'Counting what needs your check…',
    pagesNotLoaded: 'Some pages could not be loaded. Start to see them.',
    startCheck: 'Start',
    continueCheck: 'Continue',
    checkPage: (k: number): string => `Page ${String(k)}`,
    checkTotal: (n: number): string => (n === 1 ? '1 page in all' : `${String(n)} pages in all`),
    nextPage: 'Next page',
    finishCheck: 'Finish',
    backToSummary: 'Back to the summary',
    doneTitle: 'Every line is checked',
    seeFeedback: 'See the feedback',
    repostScan: 'Post a new scan',
    failedWhatToDo: 'Post the file again, or post a clearer scan.',
    pagesNotRead: (k: number): string => (k === 1 ? '1 page was not read' : `${String(k)} pages were not read`),
    readyWords: (n: number): string => (n === 1 ? 'thing needs your check' : 'things need your check'),
    pagesNotReadTitle: (pages: readonly number[]): string =>
      pages.length === 1 ? `We could not read page ${String(pages[0])}` : `We could not read pages ${pages.join(', ')}`,
    pagesNotReadSub: (count: number, othersReady: boolean): string =>
      `${othersReady ? 'The other pages are ready. ' : ''}Try again, or post a clearer scan of ${count === 1 ? 'that page' : 'those pages'}.`,
    retry: 'Try again',
    homeShort: 'Home',

    // --- upload screen, post form and history (#573, #583)
    postHeading: 'Post your homework',
    dropZone: 'Drop a PDF here, or choose a file',
    postPrefix: 'Post ',
    postGeneric: 'Post homework',
    historyHeading: 'Past homework',
    historyEmpty: 'Nothing posted yet.',
    historyCap: 'Showing the latest 20.',
    historyPosted: (date: string): string => `posted ${date}`,
    historyState: (state: 'processing' | 'ready' | 'failed', checked: boolean): string =>
      checked ? 'Checked' : state === 'processing' ? 'Being read' : state === 'failed' ? 'Could not be read' : 'Needs your check',
    historyOpen: 'Open',
    historyFeedback: 'Feedback',
    historySheetFallback: 'Homework',

    // --- /homework/:uploadId
    homeworkHeading: 'Homework',
    loadingHomework: 'Loading your homework…',
    uploadAnother: 'Upload another PDF',
    fileNotReadHeading: 'This file could not be read',
    pagesHeading: 'Pages',
    noPagesYet: 'No pages are ready to show yet.',
    pageN: (n: number): string => `Page ${String(n)}`,
    someReadersFailed: 'Some pages were not fully read. Try again; what was already read is kept.',
    feedbackHeading: 'Feedback',
    everyPageConfirmed: 'Every page is confirmed, so this homework can be checked.',
    goToFeedback: 'Go to the feedback',
    choosePage: 'Choose a page to check what was read.',
    readerConsentRefused:
      'A reader was not allowed to read this page because DaVinci does not have your agreement to send your work to it.',
    reviewConsent: 'Review your consent',

    // --- read again
    readAgain: 'Read again',
    readAgainNoConsent: 'DaVinci does not have your agreement to send your work to a reader, so nothing was read again.',
    uploadNotFound: 'This upload could not be found.',
    readingAgainStatus: 'DaVinci is reading again. This screen updates by itself.',

    // --- upload screen
    unfinishedCheckHeading: 'You have a homework check in progress',
    unfinishedCheckContinue: 'Continue',
    uploadIntro:
      'Upload one PDF of your handwritten work. DaVinci has it read twice, then shows you what was read, line by line, for you to check and correct. Nothing is graded until you have confirmed a page.',
    fileNotUploadedHeading: 'The file was not uploaded',
    notPdfChoose: 'That file is not a PDF. Choose a PDF.',
    choosePdfFirst: 'Choose a PDF first.',
    consentToKeep: 'DaVinci can only keep your work while you have agreed to that.',
    chooseAHomework: 'Choose a homework',
    courseN: (courseRef: string): string => `Course ${courseRef}`,
    homeworkPdfLabel: 'Homework PDF',
    uploading: 'Uploading…',

    // --- page review
    loadingPage: 'Loading this page…',
    stillBeingRead: 'This page is still being read. This screen updates by itself.',
    couldNotReadPage:
      'This page could not be read, so there is nothing to check yet. Once the reason shown here has been dealt with, choose Read again under the list of pages.',
    noLinesRead: 'No lines were read from this page.',
    stillNeed: (count: number): string =>
      count === 0
        ? 'No lines still need your check.'
        : count === 1
          ? '1 line still needs your check.'
          : `${String(count)} lines still need your check.`,
    confirmHeading: 'Confirm this page',
    confirmedStatus: 'You have confirmed this page. If you change a line later, you will need to confirm the page again.',
    confirmingSays: 'Confirming says that the lines above are what you wrote, apart from any you skipped. Nothing is graded from this page until you have.',
    confirmPage: 'Confirm page',
    confirmAndNext: 'Confirm and go to the next page',
    confirmAndFeedback: 'Confirm and see feedback',
    confirmAndFinish: 'Confirm and finish',
    confirmPaused: 'Confirming is paused until this page can be read again. What you have typed is still here.',
    saveOrCancelFirst: 'Save or cancel your change first.',
    optionN: (slot: 1 | 2): string => `Option ${String(slot)}`,
    // The board's check screen (#648): the question over the readings, the last row, and the step dots' spoken name.
    didYouWrite: 'What does this say?',
    somethingElse: 'Something else…',
    stepAria: (k: number): string => `Line ${String(k)}`,

    // --- a line
    lineN: (n: number): string => `Line ${String(n)}`,
    // The review shows one line at a time (#619). A count of lines, never "N of M".
    linesOnPage: (count: number): string => (count === 1 ? '1 line on this page' : `${String(count)} lines on this page`),
    previousLine: 'Back',
    nextLine: 'Next',
    showAllLines: 'Show all lines',
    showOneAtATime: 'Show one line at a time',
    alsoReadFromPage: 'Also read from your page',
    cropLabel: (n: number): string => `Your handwriting for line ${String(n)}`,
    readerUnsure: 'Unsure about:',
    readerNothing: 'Nothing was read here.',
    youCorrected: 'You corrected this.',
    optionNIsRight: (slot: 1 | 2): string => `Option ${String(slot)} is right`,
    youChoseOption: (slot: 1 | 2): string => `You chose option ${String(slot)}.`,
    youChoseAReading: 'You chose a reading.',
    youConfirmed: 'You confirmed this.',
    changeThis: 'Change this',
    saved: 'Saved:',
    whatYouWrote: 'What you wrote on this line',
    saveWhatIWrote: 'Save',
    yesWhatIWrote: 'Yes',
    notWhatIWrote: 'This is not what I wrote',
    noTypeWhatIWrote: 'No — type what I wrote',
    readFromPage: 'Read from your page.',
    notUsedInCheck: 'Not used in the check.',
    change: 'Change',
    // --- #660: one line at a time, "A quick look", and the reading editor behind "Neither of these…"
    neitherOfThese: 'Neither of these…',
    isThisWhatYouWrote: 'Is this what you wrote?',
    fixTheReading: 'Fix the reading',
    fixSavedAs: 'Saved as:',
    fixHint: 'Edit the reading, or tap a symbol to add it',
    symbolsGroup: 'Math symbols',
    insertSymbol: (name: string): string => `Insert ${name}`,
    symbolName: (symbol: string): string => SYMBOL_NAMES_EN[symbol] ?? symbol,
    skipLine: 'Neither, skip this line',
    skipLineSub: 'This line stays out of the check',
    /** What a skipped line shows (#660): the learner's own statement, never a claim about what was written. */
    skippedState: 'Skipped, left out of the check',
    // Said in words, after the line on screen: "Last line", "1 line left", "2 lines left". Never a fraction.
    linesLeft: (after: number): string =>
      after <= 0 ? 'Last line' : after === 1 ? '1 line left' : `${String(after)} lines left`,
    glanceTitle: 'A quick look',
    glanceSubAgreed: 'Both readers agreed on these lines',
    glanceSubRead: 'These lines were read from your page',
    glanceChange: 'Change',
    changeLineN: (n: number): string => `Change line ${String(n)}`,
  },
  he: {
    uploadLimit: UPLOAD_LIMIT.he,
    stage: (stage: ReviewStage): string => STAGE_HE[stage],
    uploadFailure: (kind: UploadFailureKind): string => UPLOAD_FAILURE_HE[kind],
    readerFailure: (kind: RefusedReaderKind): string => READER_FAILURE_HE[kind],
    agreement: (agreement: Agreement): string => AGREEMENT_HE[agreement],
    notOnMyPageAction: 'השורה הזו לא בדף שלי',
    notOnMyPageLabel: 'לא בדף שלי',
    notOnMyPageExplanation: 'ציינת שהשורה הזו לא בדף שלך, ולכן היא מושמטת. היא נשארת ברישום, ואפשר לבטל את זה.',
    undo: 'שחזור',
    pairQuestion: 'האם זו שורה אחת או שתיים?',
    pairExplanation: 'הקוראים מצאו את אותו טקסט בשני מקומות בדף שלך, ולכן הוא מוצג כשתי שורות.',
    pairOneLine: 'שורה אחת — כתבתי אותה פעם אחת',
    pairTwoLines: 'שתי שורות — כתבתי אותה פעמיים',
    pairNeither: 'אף אחת מהן — להציג אותן בנפרד',
    pairHeading: (first: number, second: number): string => `שורות ${String(first)} ו-${String(second)}`,
    uploadFailureSentence: uploadFailureHe,
    labelFailureSentence: labelFailureHe,
    reviewFailureSentence: reviewFailureHe,
    questionWord: 'שאלה',
    noQuestion: 'אין שאלה',
    questionSource: (source: QuestionLabelSource): string => QUESTION_SOURCE_HE[source],
    changeQuestion: 'שינוי שאלה',
    questionFieldLabel: 'לאיזו שאלה שייכת השורה הזו?',
    questionFieldHint: 'לדוגמה 3 או 5ב. השורות שמתחת לזו ממשיכות אותה, עד השאלה הבאה שסימנת.',
    saveQuestion: 'שמירת השאלה',
    notPartOfQuestion: 'לא חלק משאלה',
    cancel: 'ביטול',
    chooseHomeworkLabel: 'איזה שיעורי בית?',
    chooseHomeworkMissing: 'יש לבחור איזה שיעורי בית אלה.',
    noHomeworkSetUp: 'עדיין לא הוגדרו שיעורי בית לקורסים שלך, ולכן אי אפשר להעלות דבר.',
    thisWeekHeading: 'השבוע',
    earlierSheets: 'דפים קודמים',
    nothingDue: 'אין הגשות השבוע.',
    exerciseState: (state: ExerciseState, solutionOpen: boolean): string =>
      solutionOpen ? `${EXERCISE_STATE_HE[state]}, הפתרון פתוח` : EXERCISE_STATE_HE[state],
    dueDate: (dueAt: string): string => dueDate('he-IL', dueAt),
    dueOn: (date: string): string => `להגשה עד ${date}`,
    // --- reading, ready, check, done, failed (#573)
    readingTitle: 'קוראים את הדפים',
    crumbsLabel: 'מיקום',
    pagesInUpload: (n: number): string => (n === 1 ? 'עמוד אחד' : `${String(n)} עמודים`),
    ringUnit: 'עמודים',
    pagesReadOf: (read: number, total: number): string => `עמודים שנקראו: ${String(read)}. עמודים בהעלאה: ${String(total)}.`,
    pageTileState: (n: number, state: 'read' | 'reading' | 'waiting' | 'failed'): string =>
      `עמוד ${String(n)}: ${state === 'read' ? 'נקרא' : state === 'reading' ? 'בקריאה' : state === 'failed' ? 'לא נקרא' : 'ממתין'}`,
    timeLeft: (e: { readonly kind: 'minutes'; readonly minutes: number } | { readonly kind: 'under_a_minute' }): string =>
      e.kind === 'under_a_minute' ? 'פחות מדקה' : e.minutes === 1 ? 'עוד בערך דקה' : `עוד בערך ${String(e.minutes)} דקות`,
    timeLeftBasis: 'לפי הזמן שלקחו העמודים עד עכשיו',
    canLeave: 'אפשר לצאת. הבדיקה ממשיכה.',
    backToHomework: 'חזרה לשיעורי בית',
    readyTitle: (n: number): string => (n === 1 ? 'דבר אחד מחכה לבדיקה שלך' : `${String(n)} דברים מחכים לבדיקה שלך`),
    readyAcross: (p: number): string => (p === 1 ? 'בעמוד אחד' : `בכל ${String(p)} העמודים`),
    readyNothingLeft: 'כל השורות נקראו. נותר לאשר כל עמוד.',
    readyCounting: 'סופרים מה מחכה לבדיקה שלך…',
    pagesNotLoaded: 'חלק מהעמודים לא נטענו. אפשר להתחיל ולראות אותם.',
    startCheck: 'להתחיל',
    continueCheck: 'המשך',
    checkPage: (k: number): string => `עמוד ${String(k)}`,
    checkTotal: (n: number): string => (n === 1 ? 'עמוד אחד בסך הכול' : `${String(n)} עמודים בסך הכול`),
    nextPage: 'לעמוד הבא',
    finishCheck: 'לסיום',
    backToSummary: 'חזרה לסיכום',
    doneTitle: 'כל השורות נבדקו',
    seeFeedback: 'למשוב',
    repostScan: 'להעלות סריקה חדשה',
    failedWhatToDo: 'אפשר להעלות את הקובץ שוב, או סריקה ברורה יותר.',
    pagesNotRead: (k: number): string => (k === 1 ? 'עמוד אחד לא נקרא' : `${String(k)} עמודים לא נקראו`),
    readyWords: (n: number): string => (n === 1 ? 'דבר מחכה לבדיקה שלך' : 'דברים מחכים לבדיקה שלך'),
    pagesNotReadTitle: (pages: readonly number[]): string =>
      pages.length === 1 ? `את עמוד ${String(pages[0])} לא הצלחנו לקרוא` : `את העמודים ${pages.join(', ')} לא הצלחנו לקרוא`,
    pagesNotReadSub: (count: number, othersReady: boolean): string =>
      `${othersReady ? 'שאר העמודים מוכנים. ' : ''}אפשר לנסות שוב, או להעלות סריקה ברורה יותר של ${count === 1 ? 'העמוד הזה' : 'העמודים האלה'}.`,
    retry: 'לנסות שוב',
    homeShort: 'לדף הבית',

    // --- upload screen, post form and history (#573, #583)
    postHeading: 'להעלות שיעורי בית',
    dropZone: 'לגרור לכאן PDF או לבחור קובץ',
    postPrefix: 'להעלות את ',
    postGeneric: 'להעלות שיעורי בית',
    historyHeading: 'שיעורי בית קודמים',
    historyEmpty: 'עוד לא הועלה דבר.',
    historyCap: 'מוצגים 20 האחרונים.',
    historyPosted: (date: string): string => `הועלה ${date}`,
    historyState: (state: 'processing' | 'ready' | 'failed', checked: boolean): string =>
      checked ? 'נבדק' : state === 'processing' ? 'בקריאה' : state === 'failed' ? 'לא נקרא' : 'ממתין לבדיקה שלך',
    historyOpen: 'לפתיחה',
    historyFeedback: 'משוב',
    historySheetFallback: 'שיעורי בית',

    homeworkHeading: 'שיעורי בית',
    loadingHomework: 'טוענים את שיעורי הבית שלך…',
    uploadAnother: 'העלאת קובץ PDF נוסף',
    fileNotReadHeading: 'לא ניתן היה לקרוא את הקובץ הזה',
    pagesHeading: 'עמודים',
    noPagesYet: 'עדיין אין עמודים מוכנים להצגה.',
    pageN: (n: number): string => `עמוד ${String(n)}`,
    someReadersFailed: 'חלק מהעמודים לא נקראו עד הסוף. אפשר לנסות שוב; מה שכבר נקרא נשמר.',
    feedbackHeading: 'משוב',
    everyPageConfirmed: 'כל העמודים אושרו, ולכן אפשר לבדוק את שיעורי הבית.',
    goToFeedback: 'למשוב',
    choosePage: 'יש לבחור עמוד כדי לבדוק מה נקרא.',
    readerConsentRefused: 'קורא לא הורשה לקרוא את העמוד הזה כי ל-DaVinci אין את הסכמתך לשלוח אליו את העבודה שלך.',
    reviewConsent: 'לבדיקת ההסכמה שלך',

    readAgain: 'קריאה חוזרת',
    readAgainNoConsent: 'ל-DaVinci אין את הסכמתך לשלוח את העבודה שלך לקורא, ולכן דבר לא נקרא מחדש.',
    uploadNotFound: 'לא ניתן היה למצוא את ההעלאה הזו.',
    readingAgainStatus: 'DaVinci קוראת שוב. המסך מתעדכן מעצמו.',

    unfinishedCheckHeading: 'יש בדיקת שיעורי בית שעוד לא הסתיימה',
    unfinishedCheckContinue: 'להמשך',
    uploadIntro:
      'העלו קובץ PDF אחד של העבודה שכתבת בכתב יד. DaVinci קוראת אותו פעמיים, ואז מציגה לך מה נקרא, שורה אחר שורה, כדי לבדוק ולתקן. שום דבר לא נבדק עד לאישור עמוד.',
    fileNotUploadedHeading: 'הקובץ לא הועלה',
    notPdfChoose: 'הקובץ הזה אינו PDF. יש לבחור קובץ PDF.',
    choosePdfFirst: 'יש לבחור קובץ PDF קודם.',
    consentToKeep: 'DaVinci יכולה לשמור את העבודה שלך רק כל עוד הסכמת לכך.',
    chooseAHomework: 'בחירת שיעורי בית',
    courseN: (courseRef: string): string => `קורס ${courseRef}`,
    homeworkPdfLabel: 'קובץ PDF של שיעורי הבית',
    uploading: 'מעלים…',

    loadingPage: 'טוענים את העמוד הזה…',
    stillBeingRead: 'העמוד הזה עדיין נקרא. המסך מתעדכן מעצמו.',
    couldNotReadPage:
      'לא ניתן היה לקרוא את העמוד הזה, ולכן עדיין אין מה לבדוק. אחרי שהסיבה המוצגת כאן תטופל, יש לבחור בקריאה חוזרת מתחת לרשימת העמודים.',
    noLinesRead: 'לא נקראו שורות מהעמוד הזה.',
    stillNeed: (count: number): string =>
      count === 0
        ? 'אין שורות שממתינות לבדיקה שלך.'
        : count === 1
          ? 'שורה אחת ממתינה לבדיקה שלך.'
          : `${String(count)} שורות ממתינות לבדיקה שלך.`,
    confirmHeading: 'אישור העמוד',
    confirmedStatus: 'אישרת את העמוד הזה. אם תשונה שורה בהמשך, יהיה צורך לאשר את העמוד שוב.',
    confirmingSays: 'האישור אומר שהשורות שלמעלה הן מה שכתבת, חוץ משורות שדילגת עליהן. שום דבר לא נבדק מהעמוד הזה עד לאישור.',
    confirmPage: 'אישור העמוד',
    confirmAndNext: 'אישור ומעבר לעמוד הבא',
    confirmAndFeedback: 'אישור ומעבר למשוב',
    confirmAndFinish: 'אישור וסיום',
    confirmPaused: 'האישור מושהה עד שאפשר יהיה לקרוא את העמוד הזה שוב. מה שהקלדת עדיין כאן.',
    saveOrCancelFirst: 'יש לשמור או לבטל קודם את השינוי.',
    optionN: (slot: 1 | 2): string => `אפשרות ${String(slot)}`,
    didYouWrite: 'מה כתוב כאן?',
    somethingElse: 'משהו אחר…',
    stepAria: (k: number): string => `שורה ${String(k)}`,

    lineN: (n: number): string => `שורה ${String(n)}`,
    linesOnPage: (count: number): string => (count === 1 ? 'שורה אחת בעמוד הזה' : `${String(count)} שורות בעמוד הזה`),
    previousLine: 'לשורה הקודמת',
    nextLine: 'לשורה הבאה',
    showAllLines: 'הצגת כל השורות',
    showOneAtATime: 'הצגת שורה אחת בכל פעם',
    alsoReadFromPage: 'עוד שורות שנקראו מהדף',
    cropLabel: (n: number): string => `כתב היד שלך בשורה ${String(n)}`,
    readerUnsure: 'יש ספק לגבי:',
    readerNothing: 'כאן לא נקרא דבר.',
    youCorrected: 'תיקנת את השורה הזו.',
    optionNIsRight: (slot: 1 | 2): string => `אפשרות ${String(slot)} נכונה`,
    youChoseOption: (slot: 1 | 2): string => `בחרת באפשרות ${String(slot)}.`,
    youChoseAReading: 'בחרת באחת הקריאות.',
    youConfirmed: 'אישרת את השורה הזו.',
    changeThis: 'שינוי',
    saved: 'נשמר:',
    whatYouWrote: 'מה שכתבת בשורה הזו',
    saveWhatIWrote: 'שמירה',
    yesWhatIWrote: 'כן',
    notWhatIWrote: 'זה לא מה שכתבתי',
    noTypeWhatIWrote: 'לא — להקליד מה שכתבתי',
    readFromPage: 'נקרא מהעמוד שלך.',
    notUsedInCheck: 'לא נעשה בשורה הזו שימוש בבדיקה.',
    change: 'שינוי',
    // --- #660
    neitherOfThese: 'אף אחת מהן…',
    isThisWhatYouWrote: 'זה מה שכתוב כאן?',
    fixTheReading: 'לתקן את הקריאה',
    fixSavedAs: 'כך זה יישמר:',
    fixHint: 'אפשר לערוך את הקריאה, או להוסיף סמלים מכאן',
    symbolsGroup: 'סמלי מתמטיקה',
    insertSymbol: (name: string): string => `להוסיף ${name}`,
    symbolName: (symbol: string): string => SYMBOL_NAMES_HE[symbol] ?? symbol,
    skipLine: 'אף אחת מהן, לדלג על השורה',
    skipLineSub: 'השורה הזו נשארת מחוץ לבדיקה',
    skippedState: 'השורה דולגה ונשארת מחוץ לבדיקה',
    linesLeft: (after: number): string =>
      after <= 0 ? 'השורה האחרונה' : after === 1 ? 'נשארה שורה אחת' : `נשארו ${String(after)} שורות`,
    glanceTitle: 'מבט קצר',
    glanceSubAgreed: 'שני הקוראים הסכימו על השורות האלה',
    glanceSubRead: 'השורות האלה נקראו מהעמוד שלך',
    glanceChange: 'לשנות',
    changeLineN: (n: number): string => `לשנות את שורה ${String(n)}`,
  },
});

export type HomeworkLabels = (typeof homeworkLabels)['en'];

/** The homework wording in the interface language. */
export function useHomeworkLabels(): HomeworkLabels {
  return useMessages(homeworkLabels);
}

/** For code outside React: the wording in one language. */
export function homeworkLabelsFor(locale: UiLocale): HomeworkLabels {
  return homeworkLabels[locale];
}
